import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ShieldCheck, Link2, Umbrella, ChevronDown } from "lucide-react";
import { useSiteContent, useSiteCopy, assetUrl, phoneHref, firstName } from "@/lib/siteContent";

export default function Hero() {
  const { tx, href } = useSiteCopy();
  const { hero, business, owners, visible } = useSiteContent();
  const lines = hero.lines.map((text, i) => ({ text, cls: i === hero.lines.length - 1 ? "chrome-text-blue" : "chrome-text" }));
  const { scrollY } = useScroll();
  const badgeY = useTransform(scrollY, [0, 700], [0, -90]);
  const badgeRotate = useTransform(scrollY, [0, 700], [0, 14]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{
          backgroundImage: `url(${JSON.stringify(assetUrl(hero.image))})`,
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/70 via-navy/80 to-navy-deep" aria-hidden="true" />
      <div
        className="absolute -top-40 right-0 w-[700px] h-[700px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #00E5FF 0%, transparent 65%)" }}
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-16 items-center w-full">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-electric font-semibold tracking-[0.3em] text-sm uppercase mb-6"
            data-testid="hero-eyebrow"
          >
            {hero.eyebrow}
          </motion.p>

          <h1 className="font-bebas text-5xl sm:text-6xl md:text-8xl leading-[0.95]" data-testid="hero-heading">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-6 text-slate-400 text-base md:text-lg max-w-md font-light"
          >
            {hero.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={href("hero-call", phoneHref(owners[0].phone))}
              data-testid="hero-call-cody"
              className="flex items-center gap-3 bg-electric text-navy-deep font-bold px-8 py-4 rounded-full text-base hover:scale-105 transition-transform duration-300 glow-blue"
            >
              <Phone className="w-5 h-5" />
              {tx("hero-call", `Call ${firstName(owners[0])} — ${owners[0].phone}`)}
            </a>
            {href("hero-request", "#request") && <a
              data-testid="hero-request-button"
              href={href("hero-request", "#request")}
              className="px-8 py-4 rounded-full border border-slate-600 text-slate-200 font-semibold hover:border-electric hover:text-electric transition-colors duration-300"
            >
              {tx("hero-request", "Request Service")}
            </a>}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.25 }}
            className="mt-12 flex flex-wrap gap-6 text-xs tracking-[0.25em] text-slate-400 uppercase"
            data-testid="hero-badges"
          >
            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-electric" /> {tx("hero-licensed", "Licensed")}</span>
            <span className="flex items-center gap-2"><Link2 className="w-4 h-4 text-electric" /> {tx("hero-bonded", "Bonded")}</span>
            <span className="flex items-center gap-2"><Umbrella className="w-4 h-4 text-electric" /> {tx("hero-insured", "Insured")}</span>
          </motion.div>
        </div>

        <motion.div style={{ y: badgeY, rotate: badgeRotate, opacity: fade }} className="hidden lg:flex justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.img
              src={assetUrl(business.logo)}
              alt={`${business.name} — licensed, bonded, insured badge`}
              data-testid="hero-logo-badge"
              className="w-[420px] h-[420px] object-contain drop-shadow-[0_0_60px_rgba(0,229,255,0.25)]"
              animate={{ y: [0, -16, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </motion.div>
      </div>

      {visible.services && <motion.a
        data-testid="hero-scroll-indicator"
        href={href("hero-scroll", "#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 hover:text-electric transition-colors"
        aria-label="Scroll to services"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ChevronDown className="w-8 h-8" />
        </motion.div>
      </motion.a>}
    </section>
  );
}
